import { createColumnHelper } from "@tanstack/react-table";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import ResourceSelect from "../../../../components/Inputs/ResourceSelect";
import Select from "../../../../components/Inputs/Select";
import {
    getSettingsResourceSearchFilters,
    renderSettingsResourceLabel,
} from "../../../../utils/helpers/settings";
import { useIsMasterCompany } from "../../../../utils/hooks/company";
import { useCommonColumns } from "../../../../utils/hooks/useCommonColumns";
import { getAllocatedResources, manageAbilityOptions, resourceTypeOptions } from "./constants";

const columnHelper = createColumnHelper();

const renderMultipleItems = (labels) => {
    if (!(labels?.length > 0)) {
        return null;
    }

    return (
        <div
            style={{
                display: "grid",
                gridTemplateColumns: "max-content max-content",
                gap: "4px",
                justifyContent: "start",
            }}
        >
            {labels.map((label, index) => (
                <span
                    key={`${label}-${index}`}
                    className="border rounded border-primary px-3 py-2 text-primary text-center"
                    style={{
                        textOverflow: "ellipsis",
                        width: "100px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                    }}
                    title={label}
                >
                    {label}
                </span>
            ))}
        </div>
    );
};

const renderAllocatedResource = (row, t) => {
    const isHourType = row?.type === "hour-type";
    const labels = getAllocatedResources(row).map((item) => {
        return isHourType ? t(item) : renderSettingsResourceLabel(item);
    });
    return renderMultipleItems(labels);
};

const renderAllocatedRoles = (roles) => {
    const labels = roles.map((role) => {
        return renderSettingsResourceLabel(role);
    });
    return renderMultipleItems(labels);
};

export const useColumns = ({ edit, remove, clone }) => {
    const { t } = useTranslation();
    const isMasterCompany = useIsMasterCompany();
    const { actions } = useCommonColumns({ edit, remove, clone });

    return useMemo(() => {
        const columns = [
            columnHelper.accessor("type", {
                enableColumnFilter: true,
                header: t("type"),
                cell: (info) => {
                    const value = info.getValue();
                    const option = resourceTypeOptions.find((item) => item.value === value);
                    return option ? t(option.label) : value;
                },
                filterType: "select",
                Filter: (
                    <Select showSearch>
                        {resourceTypeOptions.map(({ value, label }) => (
                            <Select.Option key={value} value={value}>
                                {t(label)}
                            </Select.Option>
                        ))}
                    </Select>
                ),
            }),
            columnHelper.accessor("resourceIds", {
                enableColumnFilter: false,
                header: t("resource"),
                cell: (info) => renderAllocatedResource(info.row.original, t),
            }),
            columnHelper.accessor("manageAbility", {
                enableColumnFilter: true,
                header: t("manage-ability"),
                cell: (info) => {
                    const value = info.getValue();
                    const option = manageAbilityOptions.find((item) => item.value === value);
                    return option ? t(option.label) : value ? t(value) : null;
                },
                filterType: "select",
                Filter: (
                    <Select showSearch>
                        {manageAbilityOptions.map(({ value, label }) => (
                            <Select.Option key={value} value={value}>
                                {t(label)}
                            </Select.Option>
                        ))}
                    </Select>
                ),
            }),
            columnHelper.accessor("roles", {
                enableColumnFilter: true,
                header: t("role"),
                cell: (info) => renderAllocatedRoles(info.getValue()),
                filterType: "resource-select",
                Filter: (
                    <ResourceSelect
                        labelPropName="description"
                        resourcePath="/user-roles"
                        renderLabel={renderSettingsResourceLabel}
                        hasSearch
                        getSearchFilters={getSettingsResourceSearchFilters}
                    />
                ),
            }),
        ];

        if (isMasterCompany && actions) {
            columns.push(actions);
        }

        return columns;
    }, [actions, isMasterCompany, t]);
};
